(() => {
  const ELEMENT_NAME = 'mobile-sticky-add-to-cart';

  if (customElements.get(ELEMENT_NAME)) {
    return;
  }

  class MobileStickyAddToCart extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';

      this.form =
        this.closest('form[action*="/cart/add"]') ||
        this.closest('form');

      if (!this.form) {
        return;
      }

      this.mainButton = this.form.querySelector(
        '.js-main-product-add-to-cart'
      );

      this.stickyButton = this.querySelector(
        '[data-sticky-submit]'
      );

      this.stickyButtonText = this.querySelector(
        '[data-sticky-button-text]'
      );

      this.stickyPrice = this.querySelector(
        '[data-sticky-price]'
      );

      this.purchaseBlock =
        this.mainButton?.closest('.product-add-to-cart') ||
        this.mainButton;

      this.productRoot =
        this.form.closest('[data-section-type="product"]') ||
        this.form.closest('.shopify-section') ||
        this.form.parentElement;

      if (
        !this.mainButton ||
        !this.stickyButton ||
        !this.purchaseBlock
      ) {
        return;
      }

      this.mobileMedia = window.matchMedia(
        '(min-width: 0px)'
      );

      this.scrollFrame = null;
      this.syncTimeouts = [];

      this.handleStickyClick =
        this.handleStickyClick.bind(this);

      this.handleScroll =
        this.handleScroll.bind(this);

      this.handleStateChange =
        this.handleStateChange.bind(this);

      this.stickyButton.addEventListener(
        'click',
        this.handleStickyClick
      );

      this.form.addEventListener(
        'change',
        this.handleStateChange,
        true
      );

      this.form.addEventListener(
        'input',
        this.handleStateChange,
        true
      );

      this.form.addEventListener(
        'click',
        this.handleStateChange,
        true
      );

      window.addEventListener(
        'scroll',
        this.handleScroll,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        this.handleScroll,
        { passive: true }
      );

      this.mobileMedia.addEventListener(
        'change',
        this.handleScroll
      );

      this.observeProductChanges();

      this.sync();
      this.updateVisibility();
    }

    disconnectedCallback() {
      this.stickyButton?.removeEventListener(
        'click',
        this.handleStickyClick
      );

      this.form?.removeEventListener(
        'change',
        this.handleStateChange,
        true
      );

      this.form?.removeEventListener(
        'input',
        this.handleStateChange,
        true
      );

      this.form?.removeEventListener(
        'click',
        this.handleStateChange,
        true
      );

      window.removeEventListener(
        'scroll',
        this.handleScroll
      );

      window.removeEventListener(
        'resize',
        this.handleScroll
      );

      this.mobileMedia?.removeEventListener(
        'change',
        this.handleScroll
      );

      this.observer?.disconnect();

      if (this.scrollFrame) {
        window.cancelAnimationFrame(
          this.scrollFrame
        );
      }

      this.clearSyncTimeouts();
    }

    handleStickyClick() {
      this.sync();

      if (
        this.mainButton.disabled ||
        this.mainButton.classList.contains('disabled')
      ) {
        return;
      }

      this.mainButton.click();
    }

    handleScroll() {
      if (this.scrollFrame) {
        window.cancelAnimationFrame(
          this.scrollFrame
        );
      }

      this.scrollFrame =
        window.requestAnimationFrame(() => {
          this.updateVisibility();
        });
    }

    handleStateChange() {
      this.scheduleSync();
    }

    clearSyncTimeouts() {
      this.syncTimeouts.forEach((timeout) => {
        window.clearTimeout(timeout);
      });

      this.syncTimeouts = [];
    }

    scheduleSync() {
      this.clearSyncTimeouts();

      [0, 100, 350, 700].forEach((delay) => {
        const timeout = window.setTimeout(
          () => this.sync(),
          delay
        );

        this.syncTimeouts.push(timeout);
      });
    }

    updateVisibility() {
      if (!this.mobileMedia.matches) {
        this.setVisible(false);
        return;
      }

      const purchaseRect =
        this.purchaseBlock.getBoundingClientRect();

      const shouldShow =
        purchaseRect.bottom <= 0;

      this.setVisible(shouldShow);
    }

    setVisible(visible) {
      this.classList.toggle(
        'is-visible',
        visible
      );

      this.setAttribute(
        'aria-hidden',
        visible ? 'false' : 'true'
      );

      document.documentElement.classList.toggle(
        'mobile-sticky-atc-visible',
        visible
      );
    }

    sync() {
      this.syncButton();
      this.syncPrice();
    }

    syncButton() {
      const isDisabled =
        this.mainButton.disabled ||
        this.mainButton.classList.contains('disabled');

      this.stickyButton.disabled =
        isDisabled;

      this.stickyButton.classList.toggle(
        'is-disabled',
        isDisabled
      );

      const mainText =
        this.mainButton.querySelector(
          '[data-loading]'
        ) ||
        this.mainButton.querySelector(
          '[data-text] > span'
        ) ||
        this.mainButton.querySelector(
          '[data-text]'
        );

      if (
        mainText &&
        this.stickyButtonText
      ) {
        this.stickyButtonText.textContent =
          mainText.textContent.trim();
      }

      const isLoading =
        this.mainButton.classList.contains(
          'is-loading'
        );

      this.stickyButton.classList.toggle(
        'is-loading',
        isLoading
      );
    }

    getMoneyElements(container) {
      if (!container) {
        return [];
      }

      const moneyPattern =
        /(?:[$€£₴]\s?\d[\d,.]*|\d[\d\s,.]*\s?(?:USD|EUR|GBP|UAH|CAD|AUD))/i;

      return Array.from(
        container.querySelectorAll('*')
      ).filter((element) => {
        if (element.children.length > 0) {
          return false;
        }

        const text =
          element.textContent.trim();

        if (!moneyPattern.test(text)) {
          return false;
        }

        return Boolean(
          element.offsetWidth ||
          element.offsetHeight ||
          element.getClientRects().length
        );
      });
    }

    isOldPrice(element) {
      if (!element) {
        return false;
      }

      if (element.closest('s, del')) {
        return true;
      }

      const className =
        typeof element.className === 'string'
          ? element.className
          : '';

      if (
        /compare|original|old-price|was-price|regular-price|strike/i.test(
          className
        )
      ) {
        return true;
      }

      const styles =
        window.getComputedStyle(element);

      return styles.textDecorationLine.includes(
        'line-through'
      );
    }

    findSelectedBundleCard() {
      if (!this.productRoot) {
        return null;
      }

      const checkedRadios = Array.from(
        this.productRoot.querySelectorAll(
          'input[type="radio"]:checked'
        )
      ).filter((radio) => {
        return !this.contains(radio);
      });

      for (const radio of checkedRadios) {
        let element = radio.parentElement;
        let level = 0;

        while (
          element &&
          element !== this.productRoot &&
          level < 12
        ) {
          const text = element.textContent.trim();

          const allRadios = element.querySelectorAll(
            'input[type="radio"]'
          );

          const moneyElements =
            this.getMoneyElements(element);

          const isBundleOption =
            /ritual\s+essential/i.test(text);

          if (
            isBundleOption &&
            allRadios.length === 1 &&
            moneyElements.length >= 1
          ) {
            return element;
          }

          element = element.parentElement;
          level += 1;
        }
      }

      return null;
    }

    parseMoneyValue(element) {
      if (!element) {
        return 0;
      }

      const text = element.textContent
        .replace(/\s/g, '')
        .replace(/[^\d,.-]/g, '');

      if (!text) {
        return 0;
      }

      const normalized = text.includes(',') &&
        !text.includes('.')
          ? text.replace(',', '.')
          : text.replace(/,/g, '');

      return Number.parseFloat(normalized) || 0;
    }

    findSelectedBundlePrice() {
      const bundleCard =
        this.findSelectedBundleCard();

      if (!bundleCard) {
        return null;
      }

      const currentPrices =
        this.getMoneyElements(bundleCard)
          .filter((element) => {
            return !this.isOldPrice(element);
          })
          .map((element) => {
            return {
              element,
              value: this.parseMoneyValue(element)
            };
          })
          .filter((item) => {
            return item.value > 0;
          })
          .sort((first, second) => {
            return second.value - first.value;
          });

      return currentPrices[0]?.element || null;
    }

    findStandardPriceSource() {
      if (!this.productRoot) {
        return null;
      }

      const explicitSource =
        this.productRoot.querySelector(
          '[data-sticky-price-source]'
        );

      if (
        explicitSource &&
        !this.contains(explicitSource)
      ) {
        return explicitSource;
      }

      const candidates = Array.from(
        this.productRoot.querySelectorAll(
          '.price, .product-price, [data-product-price]'
        )
      ).filter((element) => {
        if (this.contains(element)) {
          return false;
        }

        return Boolean(
          element.offsetWidth ||
          element.offsetHeight ||
          element.getClientRects().length
        );
      });

      if (!candidates.length) {
        return null;
      }

      const candidatesBeforeButton =
        candidates.filter((element) => {
          return Boolean(
            element.compareDocumentPosition(
              this.mainButton
            ) &
              Node.DOCUMENT_POSITION_FOLLOWING
          );
        });

      return (
        candidatesBeforeButton.at(-1) ||
        candidates[0]
      );
    }

    syncPrice() {
      if (!this.stickyPrice) {
        return;
      }

      const bundlePrice =
        this.findSelectedBundlePrice();

      if (bundlePrice) {
        const priceText =
          bundlePrice.textContent.trim();

        if (
          this.stickyPrice.textContent.trim() !==
          priceText
        ) {
          this.stickyPrice.textContent =
            priceText;
        }

        return;
      }

      const source =
        this.findStandardPriceSource();

      if (!source) {
        return;
      }

      const clone =
        source.cloneNode(true);

      clone
        .querySelectorAll('[id]')
        .forEach((element) => {
          element.removeAttribute('id');
        });

      const newMarkup =
        clone.innerHTML ||
        clone.textContent;

      if (
        this.stickyPrice.innerHTML !==
        newMarkup
      ) {
        this.stickyPrice.innerHTML =
          newMarkup;
      }
    }

    observeProductChanges() {
      if (!this.productRoot) {
        return;
      }

      this.observer = new MutationObserver(
        (mutations) => {
          const hasExternalChange =
            mutations.some((mutation) => {
              return !this.contains(
                mutation.target
              );
            });

          if (hasExternalChange) {
            this.scheduleSync();
          }
        }
      );

      this.observer.observe(
        this.productRoot,
        {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: [
            'class',
            'disabled',
            'checked',
            'selected',
            'value'
          ]
        }
      );
    }
  }

  customElements.define(
    ELEMENT_NAME,
    MobileStickyAddToCart
  );
})();